import { motion } from "framer-motion";
import { Shield, Camera, Home, Cpu, ArrowUpRight } from "lucide-react";

const services = [
{
  id: "infrastructure",
  icon: Shield,
  title: "Secure Infrastructure",
  description:
  "Military-grade Wi-Fi and networking with enterprise firewalls, VLAN segmentation, and intrusion detection. Your network becomes an impenetrable fortress.",
  features: ["Enterprise Firewalls", "VLAN Segmentation", "Intrusion Detection"],
  accent: "from-[#5e5ce6]/20 to-transparent",
  size: "col-span-1 md:col-span-2 row-span-1"
},
{
  id: "surveillance",
  icon: Camera,
  title: "Private Surveillance",
  description: "Local-storage cameras with local AI processing with no cloud leaks. Your footage stays on your property, encrypted and accessible only to you.",

  features: ["Local NVR Storage", "E2E Encryption", "Private Access"],
  accent: "from-[#818cf8]/20 to-transparent",
  size: "col-span-1 row-span-2",
  image: "https://images.unsplash.com/photo-1608186286925-8c0e1c1fbeac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxoaWdoJTIwdGVjaCUyMHNlY3VyaXR5JTIwY2FtZXJhJTIwbGVucyUyMGNsb3NlJTIwdXAlMjBtYWNybyUyMGRhcmslMjBjaW5lbWF0aWN8ZW58MHx8fHwxNzcyNTkyNzUyfDA&ixlib=rb-4.1.0&q=85"
},
{
  id: "automation",
  icon: Home,
  title: "Hardened Automation",
  description:
  "Encrypted smart lighting, climate, and access control. Every command is secured, every action is private.",
  features: ["Smart Lighting", "Climate Control", "Access Management"],
  accent: "from-[#34c759]/20 to-transparent",
  size: "col-span-1 row-span-1",
  image: "https://images.unsplash.com/photo-1770625467384-304e461ef1be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGhvbWUlMjB0aGVybW9zdGF0JTIwZGlnaXRhbCUyMGRpc3BsYXklMjB3YWxsJTIwbW91bnRlZHxlbnwwfHx8fDE3NzI1OTI3NjR8MA&ixlib=rb-4.1.0&q=85"
},
{
  id: "ai",
  icon: Cpu,
  title: "Local AI & Home Assistant",
  description:
  "Private, voice-controlled intelligence that stays inside your walls. No Amazon, no Google, no listening ears.",
  features: ["Home Assistant", "Local Voice AI", "Zero Cloud"],
  accent: "from-[#ff9f0a]/20 to-transparent",
  size: "col-span-1 row-span-1"
}];


const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

export const ServicesGrid = () => {
  return (
    <section
      id="services"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="services-section">

      {/* Background Glow */}
      <div className="section-glow-left" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16">

          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-[#5e5ce6]" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#5e5ce6] font-medium">
              The Secure Four
            </span>
          </div>
          <h2
            className="text-4xl md:text-5xl font-bold tracking-tight text-white max-w-2xl"
            data-testid="services-headline">

            Four pillars of impenetrable home security
          </h2>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">

          {services.map((service) =>
          <motion.div
            key={service.id}
            variants={cardVariants}
            className={`group relative overflow-hidden rounded-3xl bg-white/[0.03] border border-white/[0.08] backdrop-blur-xl ${service.size} min-h-[280px] md:min-h-[320px]`}
            data-testid={`service-card-${service.id}`}>

              {/* Background Image (if exists) */}
              {service.image &&
            <div className="absolute inset-0 z-0">
                  <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-500" />

                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />
                </div>
            }

              {/* Gradient Accent */}
              <div
              className={`absolute inset-0 bg-gradient-to-br ${service.accent} opacity-50`} />


              {/* Content */}
              <div className="relative z-10 h-full p-8 flex flex-col">
                {/* Icon */}
                <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors duration-300">
                  <service.icon className="w-6 h-6 text-[#5e5ce6]" />
                </div>

                {/* Title */}
                <h3 className="text-xl md:text-2xl font-semibold text-white mb-3">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-white/50 leading-relaxed mb-6 flex-grow">
                  {service.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mt-auto">
                  {service.features.map((feature) =>
                <span
                  key={feature}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-white/60 border border-white/5">

                      {feature}
                    </span>
                )}
                </div>

                {/* Hover Arrow */}
                <div className="absolute top-8 right-8 w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                  <ArrowUpRight className="w-4 h-4 text-white/60" />
                </div>
              </div>

              {/* Card shine effect */}
              <div className="card-shine" />
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};